import type { BillOut } from "../../../api/bills";
// Знакова конвенция на процента по сметка (bill.discount_percent):
// положителен = отстъпка на клиент, отрицателен = надбавка, null = без
// клиент (0 е валиден клиент с 0%). BE прилага
// price × (100 − percent) / 100 върху всеки нов ред (заключените цени се
// прескачат) — виж bills/service.py. Споделено между двата екрана на
// „Маркиране" (ClientsModal, каталога и прегледа на поръчката).

/** „−X%" за отстъпка / „+X%" за надбавка / „0%" без знак за клиент с 0%. */
export const formatSignedBillPercent = (percent: number): string =>
  percent === 0
    ? "0%"
    : percent < 0
      ? `+${Math.abs(percent)}%`
      : `-${percent}%`;

/** Единична цена след процента по сметката (BE формулата). */
export const applyBillPercentToPrice = (
  price: number,
  percent: number,
): number => (price * (100 - percent)) / 100;

/**
 * Средният процент на отстъпките/надбавките по редовете — за картата на масата,
 * когато няма клиент с процент. Претеглен по сума: свалената сума спрямо цената
 * по меню (3 × 10 €, един ред с 60% → 20%; и трите с 60% → 60%). BE смята
 * `total_discount` без сторнираните редове и без „Промяна на цена", а
 * `total_amount` е платимото — сборът им е цената по меню. 0 — няма корекция.
 * Знакът е като на клиента: >0 отстъпка, <0 надбавка.
 */
export const averageLinePercentOf = (
  bill: Pick<BillOut, "total_amount" | "total_discount" | "bill_discount_applied">,
): number => {
  const discount = parseFloat(bill.total_discount || "0") || 0;
  // Отстъпката в € на сметката е свой бадж и не е в `total_discount`, но е
  // извадена от `total_amount` — връща се, иначе процентът излиза надут.
  const billDiscount = parseFloat(String(bill.bill_discount_applied ?? 0)) || 0;
  const menuTotal =
    (parseFloat(bill.total_amount || "0") || 0) + discount + billDiscount;
  if (discount === 0 || menuTotal <= 0) return 0;
  return Math.round((discount / menuTotal) * 1000) / 10;
};

/** Процентът на закачения клиент и спестената/надбавената сума; null — без клиент. */
export const customerDiscountOf = (
  bill: Pick<BillOut, "discount_applied" | "discount_percent" | "total_discount">,
) => {
  const applied = bill.discount_applied;
  if (!applied) return null;
  const percent = applied.percent ?? bill.discount_percent ?? 0;
  return {
    percent,
    isSurcharge: percent < 0,
    // total_discount е знаков: положителен при отстъпка, отрицателен при
    // надбавка — показваме абсолютната стойност с подходящ етикет.
    amount: Math.abs(parseFloat(bill.total_discount || "0")),
  };
};
